import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { type Lesson, type Word, type LanguageLevel } from '../../types';
import { supabase, getLessonsTable } from '../../utils/supabase';
import { LessonForm } from './LessonForm';
import { WordForm } from './WordForm';
import { LessonCard } from './LessonCard';

interface LessonManagementProps {
  lessons: Lesson[];
  activeLevel: LanguageLevel;
  language: string;
  onSaveLesson: (lessonData: Partial<Lesson>) => Promise<void>;
  onDeleteLesson: (lessonId: string) => void;
  onSaveWord: (word: Partial<Word>, lessonId: string) => Promise<void>;
  onDeleteWord: (wordId: string) => void;
}

export function LessonManagement({
  lessons,
  activeLevel,
  language,
  onSaveLesson,
  onDeleteLesson,
  onSaveWord,
  onDeleteWord
}: LessonManagementProps) {
  const [editingLesson, setEditingLesson] = useState<Partial<Lesson> | null>(null);
  const [editingWord, setEditingWord] = useState<Partial<Word> | null>(null);
  const [expandedLessons, setExpandedLessons] = useState<string[]>([]);
  const [currentLessonId, setCurrentLessonId] = useState<string | null>(null);

  const toggleLessonExpansion = (lessonId: string) => {
    setExpandedLessons(prev => 
      prev.includes(lessonId) 
        ? prev.filter(id => id !== lessonId)
        : [...prev, lessonId]
    );
  };

  const handleSaveWord = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingWord || !currentLessonId) return;
    
    try {
      await onSaveWord(editingWord, currentLessonId);
      setEditingWord(null);
      setCurrentLessonId(null);
    } catch (error) {
      console.error('Error saving word:', error);
      alert('Error saving word. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button
          onClick={async () => {
            const table = getLessonsTable(language);
            const { data } = await supabase
              .from(table)
              .select('order_index')
              .eq('level', activeLevel)
              .order('order_index', { ascending: false })
              .limit(1)
              .maybeSingle();

            const nextOrderIndex = data ? (data.order_index || 0) + 1 : 1;

            setEditingLesson({
              title: '',
              description: '',
              text: '',
              category: '',
              difficulty: 'easy',
              words: [],
              order_index: nextOrderIndex,
              show_text: false,
              level: activeLevel
            });
          }}
          className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add New Lesson
        </button>
      </div>

      {editingLesson && (
        <LessonForm
          lesson={editingLesson}
          onSave={onSaveLesson}
          onChange={setEditingLesson}
          onClose={() => setEditingLesson(null)}
        />
      )}

      {editingWord && (
        <WordForm
          word={editingWord}
          onSave={handleSaveWord}
          onChange={setEditingWord}
          onClose={() => {
            setEditingWord(null);
            setCurrentLessonId(null);
          }}
        />
      )}

      <div className="space-y-4">
        {lessons.map((lesson) => (
          <LessonCard
            key={lesson.id}
            lesson={lesson}
            isExpanded={expandedLessons.includes(lesson.id)}
            onToggleExpand={() => toggleLessonExpansion(lesson.id)}
            onEditLesson={() => setEditingLesson(lesson)}
            onDeleteLesson={() => onDeleteLesson(lesson.id)}
            onAddWord={() => {
              setEditingWord({
                dutch: '',
                translation: '',
                pronunciation: '',
                example: '',
                example_translation: ''
              });
              setCurrentLessonId(lesson.id);
            }}
            onEditWord={(word) => {
              setEditingWord(word);
              setCurrentLessonId(lesson.id);
            }}
            onDeleteWord={onDeleteWord}
          />
        ))}

        {lessons.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <p className="text-gray-500">No lessons found</p>
            <button
              onClick={() => setEditingLesson({
                title: '',
                description: '',
                text: '',
                category: '',
                difficulty: 'easy',
                words: []
              })}
              className="mt-4 inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create First Lesson
            </button>
          </div>
        )}
      </div>
    </div>
  );
}